import type { Components } from 'react-markdown';
import { Activity } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { NormalizedMcc } from '@ballerine/common';

import { CopyButton } from '@/components';
import { ReportWrapper } from '../../report-shared/components';

type OperationalAnalysisSectionProps = {
  lineOfBusiness?: string | null;
  lineOfBusinessReasoning?: string | null;
  mcc?: NormalizedMcc | null;
};

export const OperationalActivitySection = ({
  lineOfBusiness,
  mcc,
  lineOfBusinessReasoning,
}: OperationalAnalysisSectionProps) => {
  if (!lineOfBusiness && !mcc?.code) {
    return null;
  }

  return (
    <ReportWrapper
      title="Operational Analysis"
      Icon={Activity}
      tooltip="Business operations analysis including line of business and merchant category classification."
      showTooltip={false}
    >
      <p className="-mt-3 mb-3">Based on Visa Merchant Data Standards Manual</p>
      <div className="grid grid-cols-2 gap-6">
        <ActivityItem
          category="Line Of Business"
          title={lineOfBusiness}
          description={null}
          reasoning={lineOfBusinessReasoning}
        />
        <ActivityItem
          category="MCC"
          title={mcc?.code}
          description={mcc?.category}
          reasoning={mcc?.reasoning}
        />
      </div>
    </ReportWrapper>
  );
};

const ActivityItem = ({
  category,
  title,
  description,
  reasoning,
}: {
  category: string;
  title?: string | null;
  description?: string | null;
  reasoning?: string | null;
}) => {
  if (!title) {
    return null;
  }

  return (
    <div className="space-y-4 rounded-lg border p-5">
      <div className="inline-block rounded-sm bg-slate-400/20 px-2 py-0.5 text-sm font-medium uppercase tracking-tight text-slate-400">
        {category}
      </div>

      <div>
        <div>
          <p className="text-xl font-semibold text-slate-900">{title}</p>

          {description && (
            <div className="flex space-x-2 text-justify text-sm leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {description}
              </ReactMarkdown>
              <CopyButton className="inline" textToCopy={`${title} - ${description}`} />
            </div>
          )}

          {reasoning && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {reasoning}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
};

const markdownComponents: Components = {
  p: ({ node, ...props }) => (
    <p className="my-3 text-justify text-sm leading-6 text-slate-800" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="mb-1 mt-2 text-lg font-semibold text-slate-800" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mb-1 mt-2 text-base font-semibold text-slate-800" {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="my-3 list-disc space-y-2 pl-6" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-3 list-decimal space-y-2 pl-6" {...props} />,
  li: ({ node, ...props }) => <li className="max-w-[90ch] pl-1.5 text-sm leading-6" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="mx-1 inline-block rounded-full bg-indigo-50 px-2 py-0 text-[0.7rem] text-neutral-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-3 border-l-2 pl-4 italic"
      style={{ borderColor: '#EFF2FF', color: '#14203D' }}
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <div className="my-5 w-full">
      <hr
        className="relative -mx-6"
        style={{ borderColor: '#EFF2FF', width: 'calc(100% + 3rem)' }}
        {...props}
      />
      <div
        className="relative -mx-6 h-4"
        style={{
          width: 'calc(100% + 3rem)',
          background: 'linear-gradient(0deg, #FFFFFF 0%, rgba(244, 246, 253, 0.4) 100%)',
        }}
      />
    </div>
  ),
  table: ({ node, ...props }) => (
    <table className="my-4 w-full border-collapse text-sm" {...props} />
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr className="border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-3 py-2 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-3 py-2 align-top" {...props} />
  ),
};
